import React, { useEffect, useState } from 'react';

export const CustomCursor: React.FC = () => {
  const [position, setPosition] = useState({ x: -100, y: -100 });
  const [trailingPos, setTrailingPos] = useState({ x: -100, y: -100 });
  const [isHovered, setIsHovered] = useState(false);
  const [isPointer, setIsPointer] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Only run cursor follower on non-touch devices
    if (window.matchMedia('(pointer: coarse)').matches) {
      return;
    }

    setIsVisible(true);

    const handleMouseMove = (e: MouseEvent) => {
      setPosition({ x: e.clientX, y: e.clientY });

      // Check if hovering interactive element
      const target = e.target as HTMLElement | null;
      if (target) {
        const isClickable = !!target.closest('a, button, input, textarea, .tech-card, .about-info-card, .project-card, .filter-btn, [role="button"]');
        setIsPointer(isClickable);
      }
    };

    const handleMouseDown = () => setIsHovered(true);
    const handleMouseUp = () => setIsHovered(false);
    const handleMouseLeave = () => setIsVisible(false);
    const handleMouseEnter = () => setIsVisible(true);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseenter', handleMouseEnter);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseenter', handleMouseEnter);
    };
  }, []);

  // Smooth lerp trailing animation for outer ring
  useEffect(() => {
    if (!isVisible) return;
    let animationFrameId: number;

    const followCursor = () => {
      setTrailingPos((prev) => {
        const dx = position.x - prev.x;
        const dy = position.y - prev.y;
        return {
          x: prev.x + dx * 0.18,
          y: prev.y + dy * 0.18
        };
      });
      animationFrameId = requestAnimationFrame(followCursor);
    };

    animationFrameId = requestAnimationFrame(followCursor);
    return () => cancelAnimationFrame(animationFrameId);
  }, [position, isVisible]);

  if (!isVisible) return null;

  return (
    <>
      {/* Outer Glowing Ring */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: isPointer ? '54px' : '36px',
          height: isPointer ? '54px' : '36px',
          borderRadius: '50%',
          border: isPointer ? '1.5px solid var(--accent-cyan)' : '1px solid rgba(0, 242, 254, 0.4)',
          background: isPointer ? 'rgba(0, 242, 254, 0.08)' : 'rgba(0, 242, 254, 0.03)',
          boxShadow: isPointer ? '0 0 25px rgba(0, 242, 254, 0.35)' : 'none',
          transform: `translate3d(${trailingPos.x - (isPointer ? 27 : 18)}px, ${trailingPos.y - (isPointer ? 27 : 18)}px, 0) scale(${isHovered ? 0.85 : 1})`,
          pointerEvents: 'none',
          zIndex: 9999,
          transition: 'width 0.25s ease, height 0.25s ease, border 0.25s ease, background 0.25s ease',
          backdropFilter: isPointer ? 'blur(2px)' : 'none'
        }}
      />
      {/* Inner Precision Dot */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: isPointer ? '8px' : '6px',
          height: isPointer ? '8px' : '6px',
          borderRadius: '50%',
          background: 'var(--accent-cyan)',
          boxShadow: '0 0 12px var(--accent-cyan)',
          transform: `translate3d(${position.x - (isPointer ? 4 : 3)}px, ${position.y - (isPointer ? 4 : 3)}px, 0)`,
          pointerEvents: 'none',
          zIndex: 10000,
          transition: 'width 0.2s ease, height 0.2s ease'
        }}
      />
    </>
  );
};
